/**
 * Neutral S012 conversation-time formatter.
 *
 * The refreshed design proves exactly three presentation classes: same-day
 * clock time, `вчера`, and a lower-case Russian weekday. Older/future values
 * fail closed because S012 provides no authority for a calendar-date format.
 */

const PRESENTATION_TIME_ZONE = 'Europe/Moscow';
const MILLISECONDS_PER_DAY = 86_400_000;
const RUSSIAN_WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'] as const;

interface DateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: PRESENTATION_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function zonedParts(date: Date): DateParts | null {
  const values = new Map(
    partsFormatter
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  );
  const year = values.get('year');
  const month = values.get('month');
  const day = values.get('day');
  const hour = values.get('hour');
  const minute = values.get('minute');

  if ([year, month, day, hour, minute].some((value) => !Number.isFinite(value))) {
    return null;
  }
  return {
    year: year as number,
    month: month as number,
    day: day as number,
    hour: hour as number,
    minute: minute as number,
  };
}

function calendarSerial(parts: DateParts): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day) / MILLISECONDS_PER_DAY;
}

/** Format a timestamp relative to an explicit reference timestamp. */
export function formatConversationTime(
  lastActivityAt: string,
  referenceTime: string,
): string {
  const activity = new Date(lastActivityAt);
  const reference = new Date(referenceTime);
  if (!Number.isFinite(activity.getTime()) || !Number.isFinite(reference.getTime())) {
    return '—';
  }

  const activityParts = zonedParts(activity);
  const referenceParts = zonedParts(reference);
  if (activityParts === null || referenceParts === null) {
    return '—';
  }

  const dayDifference = calendarSerial(referenceParts) - calendarSerial(activityParts);
  if (dayDifference === 0 && activity.getTime() <= reference.getTime()) {
    return `${String(activityParts.hour).padStart(2, '0')}:${String(activityParts.minute).padStart(2, '0')}`;
  }
  if (dayDifference === 1) {
    return 'вчера';
  }
  if (dayDifference >= 2 && dayDifference <= 6) {
    const localCalendarDate = new Date(
      Date.UTC(activityParts.year, activityParts.month - 1, activityParts.day),
    );
    return RUSSIAN_WEEKDAYS[localCalendarDate.getUTCDay()] ?? '—';
  }
  return '—';
}
